<template>
  <div ref="rootElement" class="zt-input-main w-full" :class="[textColorClasses, inputClasses]"
    :style="[textColorStyle]">
    <div class="zt-input-slot relative flex w-full items-center">
      <div v-if="prependIcon && !appendIcon" class="zt-input-prepended-icon flex justify-center"
        :class="{ 'zt-input-appended-icon-disabled': isDisabled }">
        <zt-icon :name="prependIcon" />
      </div>
      <fieldset v-if="outlined" aria-hidden="true"
        class="pointer-events-none absolute bottom-0 left-0 right-0 border-collapse border-zt-xs border-solid border-zt-dark-300">
        <legend class="p-0 text-left" :style="{ width: legendWidth }">
          <span>&#8203;</span>
        </legend>
      </fieldset>
      <div class="zt-input-text-field relative flex flex-auto items-center">
        <label :for="componentId" :class="[labelClasses]" ref="ztInputLabel"
          class="zt-label pointer-events-none absolute block truncate text-zt-dark-300">{{ label }}</label>
        <input ref="ztInput"
          class="zt-input flex w-full flex-auto bg-transparent text-zt-dark-500 outline-none"
          placeholder=" " name="zt-input" :id="componentId" :value="internalValue" autocomplete="off"
          autocapitalize="off" spellcheck="off" v-bind="$attrs"
          v-on="inputListeners"
          @focus="_onFocus" @blur="_onBlur" @input="onInput" />
      </div>
      <div v-if="appendIcon" class="zt-input-appended-icon flex justify-center"
        :class="{ 'zt-input-appended-icon-disabled': isDisabled }">
        <zt-icon :name="appendIcon" />
      </div>
      <div class="flex items-center" v-if="clearable">
        <div class="zt-input-clearable flex cursor-pointer justify-center text-zt-md"
          :class="[{ invisible: !modelValue }]" @click="clearTextFieldIcon">
          <zt-icon name="times-solid" />
        </div>
      </div>
      <div v-if="$attrs.type" class="zt-input-appended-icon cursor-pointer text-zt-dark-400" @click="togglePassword">
        <zt-icon :name="iconTogglePassword" />
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'ztInput',
  inheritAttrs: false,
  model: {
    prop: 'modelValue',
    event: 'update:modelValue',
  },
  props: {
    label: { type: String, default: '' },
    modelValue: { type: [String, Number], default: '' },
    appendIcon: { type: String, default: '' },
    prependIcon: { type: String, default: '' },
    clearable: { type: Boolean, default: false },
    outlined: { type: Boolean, default: false },
    color: { type: String, default: 'primary' },
  },
  data() {
    return {
      componentId: '',
      internalValue: this.modelValue,
      inputType: this.$attrs.type || '',
      iconTogglePassword: 'eye-slash',
      isFocused: false,
      labelWidth: 0,
    }
  },
  mounted() {
    this.componentId = this._uid
    this.$nextTick(() => { this.setLabelWidth() })
  },
  watch: {
    label() {
      this.$nextTick(() => { this.setLabelWidth() })
    },
    modelValue(newVal) {
      this.internalValue = newVal
    },
    isFocused(newVal) {
      if (newVal && this.labelWidth === 0) {
        this.$nextTick(() => { this.setLabelWidth() })
      }
    },
    isDirty(newVal) {
      if (newVal && this.labelWidth === 0) {
        this.$nextTick(() => { this.setLabelWidth() })
      }
    },
  },
  computed: {
    isDirty() {
      return this.internalValue !== null && String(this.internalValue).length > 0
    },
    isDisabled() {
      return !!this.$attrs.disabled
    },
    isInputPassword() {
      return this.inputType === 'password'
    },
    inputClasses() {
      return {
        'zt-input-focused': this.isFocused,
        'zt-input-disabled': this.isDisabled,
        'zt-input-outlined': this.outlined,
      }
    },
    labelClasses() {
      return {
        'zt-label-active': this.isFocused || this.isDirty,
        'zt-label-active--inner-icon':
          (this.isFocused && this.prependIcon) || (this.isDirty && this.prependIcon),
      }
    },
    legendWidth() {
      return this.isFocused || (this.label && this.isDirty)
        ? this.labelWidth + 'px'
        : '0px'
    },
    textColorClasses() {
      if (this.isFocused && !this.isHexColor(this.color)) {
        const colorLevel = this.color === 'primary' || this.color === 'secondary' ? '-200' : ''
        return 'text-zt-' + this.color + colorLevel
      }
      return 'text-zt-dark-400'
    },
    textColorStyle() {
      if (this.isFocused && this.isHexColor(this.color)) {
        return { color: '#' + this.color }
      }
      return ''
    },
    inputListeners() {
      const listeners = Object.assign({}, this.$listeners)
      delete listeners.blur
      delete listeners.focus
      delete listeners.input
      return listeners
    },
  },
  methods: {
    _onFocus(event) {
      const input = this.$refs.ztInput
      if (!input) return
      if (input !== document.activeElement) {
        return input.focus()
      }
      if (!this.isFocused) {
        this.isFocused = true
        this.$emit('focus', event)
      }
    },
    _onBlur(event) {
      this.isFocused = false
      this.$emit('blur', event)
    },
    togglePassword() {
      this.inputType = this.isInputPassword ? 'text' : 'password'
      this.iconTogglePassword = this.isInputPassword ? 'eye-slash' : 'eye'
      if (this.$refs.ztInput) {
        this.$refs.ztInput.setAttribute('type', this.inputType)
      }
    },
    async clearableInput() {
      await this.$nextTick()
      this.internalValue = ''
      this.$emit('update:modelValue', '')
      if (this.$refs.ztInput) this.$refs.ztInput.focus()
      this.$emit('clear')
    },
    onInput(event) {
      this.internalValue = event.target.value
      this.$emit('update:modelValue', this.internalValue)
    },
    clearTextFieldIcon() {
      if (!this.clearable) return
      this.clearableInput()
    },
    setLabelWidth() {
      if (!this.outlined || !this.$refs.ztInputLabel) return
      this.labelWidth = this.label
        ? Math.min(
          this.$refs.ztInputLabel.scrollWidth * 0.75 + 6,
          this.$refs.rootElement.offsetWidth - 24
        )
        : 0
    },
    isHexColor(color) {
      return /^#([0-9A-F]{3}){1,2}$/i.test('#' + color)
    },
  },
}
</script>

<style scoped lang="css" src="../../../../styles/components/ztInput.css" />
